import { useState } from 'react';
import type { ApprovalLineItem } from '@safetyhub/report-composer';
import { OrganizationMember } from '../../../../../lib/organizations';
import { approvalStepLabel } from '../approvalLineIdentity';
import { defaultNotifyVia, selectedNotifyKinds } from '../notifyChannelOptions';
import type { NotifyKind } from '../notifyChannelOptions';

/**
 * OrganizationMember에서 표시용 멤버 정보 추출.
 *
 * 결재라인 복원(지난 위임 기본값)도 이 변환을 써야 한다 — 저장분에는 이름을 담지 않으므로
 * (그 사이 바뀌었을 수 있다) 현재 멤버 목록에서 같은 규칙으로 채워야 편집 경로와 표시가 갈리지
 * 않는다. 그래서 export 한다.
 */
export function extractMemberDisplay(orgMember: OrganizationMember): {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  accountEmail?: string | null;
  title?: string | null;
} {
  const memberId = orgMember.member?.id ?? orgMember.memberId ?? orgMember.id;
  const name = orgMember.member?.name ?? orgMember.user?.name ?? '-';
  const phone = orgMember.member?.phone ?? orgMember.user?.phone ?? null;
  // 연락 이메일(Member.email) — 결재 통지가 읽는 축
  const email = orgMember.member?.email ?? null;
  // F-D: 계정의 **로그인 이메일**은 다른 칸이다. `member.email`이 비어 있어도 이메일 가입자는
  // 이 값으로 통지가 가므로 채널 선택지에 포함해야 한다.
  // (⚠️ 옛 주석은 "user.auths에서 추출"이라 적혀 있었지만 코드는 member.email만 봤다 — 거짓이었다.)
  const accountEmail =
    orgMember.user?.auths?.find((a) => a.provider === 'EMAIL')?.providerId ?? null;
  return { id: memberId, name, phone, email, accountEmail };
}

export type AssigneeSourceKind = 'delegatedTo' | 'role' | 'author' | 'member';

/**
 * 편집 중인 행에 **붙어 다니는** 담당자 소스 제한.
 *
 * 이 값의 출처는 preview `baseRows`이고 그쪽 축은 **index**인데, 편집 중에는 행을 지우거나
 * 옮길 수 있어 index가 어긋난다. 위치로 매번 되찾으면(=positional 배열) 앞 칸 하나를 지운
 * 순간 뒤 칸들이 **남의 제한**을 쓰게 된다. 그래서 index가 아직 baseRows와 맞는 순간
 * (`initEdit`)에 행에 새겨 두고, 이후에는 행과 함께 이동·삭제되게 한다.
 * (`_locked`도 같은 index 축에서 stamp되지만 그쪽은 locked 칸이 선두 고정이라 밀리지 않는다 —
 *  `enforceLockedFromSystem`이 `[...locked, ...unlocked]` 순으로 돌려주고, locked 칸은 편집
 *  모달에서 삭제·이동·드롭 대상이 모두 막혀 있다.)
 */
export type ApprovalLineItemWithWhitelist = ApprovalLineItem & {
  _assigneeSourceWhitelist?: AssigneeSourceKind[];
};

/**
 * 무변경 저장 게이트용 내용 지문 — 저장 payload에 실리는 축(라벨·담당자 식별자·소스·알림 채널·
 * 미지정 여부·순서)만 본다. 표시용 필드(member 이름 등)는 식별자에 따라오므로 제외한다.
 * 양쪽 지문을 같은 코드로 만들므로 키 순서가 갈릴 일이 없다.
 */
function editingLinesFingerprint(lines: ApprovalLineItem[]): string {
  return JSON.stringify(
    lines.map((line) => ({
      label: line.label ?? '',
      memberId: line.memberId ?? null,
      sourceType: line.sourceType ?? null,
      // S76-A: 지정 방식 근거도 축이다. `sourceType`이 해소 결과라, 역할 칸이 작성자로
      // 폴백해 있던 상태에서 사용자가 "작성자로 지정"을 누르면 sourceType은 그대로 'author'고
      // 바뀌는 것은 이 체인뿐이다 — 넣지 않으면 그 변경이 무변경으로 보여 저장이 막힌다.
      assignmentChain: line._assignmentChain ?? null,
      notifyVia: line.notifyVia ?? null,
      unassigned: line._assigneeUnset === true,
    })),
  );
}

export function useApprovalLineEdit(
  approvalLines: ApprovalLineItem[],
  availableMembers: OrganizationMember[],
  /**
   * 칸별 담당자 소스 화이트리스트(index = preview baseRows index). 항목이 `undefined`면 제한 없음.
   * base 범위 밖(작성자가 추가한 칸)은 배열이 짧아 자동으로 제한 없음이 된다.
   */
  assigneeSourceWhitelists?: (AssigneeSourceKind[] | undefined)[],
) {
  const [editingLines, setEditingLines] = useState<ApprovalLineItem[]>([]);
  // 편집 시작(initEdit) 시점의 내용 지문 — 무변경 저장(가짜 site 메타 이력·version 증가)을 막는
  // dirty 판정 기준. 모달을 닫으면(resetEdit) 빈 목록 지문으로 되돌리고, 재진입 시 다시 찍는다.
  const [editSnapshot, setEditSnapshot] = useState<string>(() => editingLinesFingerprint([]));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const initEdit = () => {
    const lines = approvalLines.map((line, index) => ({
      ...line,
      label: line.label || `${index + 1}단계`,
      // 정본 축(notifyVia) 하나만 시드한다(2026-08-23 단일화) — 값이 없으면(제한 없음)
      // 담당자의 보유 채널 전부를 기본 선택으로 깐다(화면 판정 defaultNotifyVia와 동형).
      notifyVia: line.notifyVia ?? defaultNotifyVia(line.member),
      // 여는 시점의 목록은 baseRows와 index가 맞는다(양쪽 다 저장 순서 그대로). 여기서만 새긴다.
      // 이미 새겨진 행은 그대로 둔다 — standalone/위임 흐름은 편집 결과가 부모 상태를 거쳐
      // 다시 내려오므로, 그때 index로 다시 매기면 지운 칸만큼 밀린 값이 덮어써진다.
      _assigneeSourceWhitelist:
        (line as ApprovalLineItemWithWhitelist)._assigneeSourceWhitelist ??
        assigneeSourceWhitelists?.[index],
    }));
    setEditingLines(lines);
    setEditSnapshot(editingLinesFingerprint(lines));
  };

  const resetEdit = () => {
    setEditingLines([]);
    setEditSnapshot(editingLinesFingerprint([]));
  };

  /**
   * 편집 시작 이후 내용이 바뀌었는가 — 저장 버튼 게이트.
   * `setEditingLines` 직접 교체 경로("현장 기본으로 되돌리기" 등)도 내용 비교라 그대로 판정된다.
   */
  const isDirty = editingLinesFingerprint(editingLines) !== editSnapshot;

  /** 회사 멤버 ID로 결재자 추가 (신규 picker) */
  const handleAddMember = (memberId: string) => {
    const orgMember = availableMembers.find((m) => (m.member?.id ?? m.memberId ?? m.id) === memberId);
    if (!orgMember) return;
    const memberDisplay = extractMemberDisplay(orgMember);
    const newIndex = editingLines.length;
    const newLevel = newIndex + 1;
    setEditingLines([
      ...editingLines,
      {
        memberId: memberDisplay.id,
        member: memberDisplay,
        approvalLevel: newLevel,
        label: `${newLevel}단계`,
        // 출처 축 제거(2026-08-23): 종류 축만 시드한다. 계정 이메일도 이메일 채널의
        // 근거로 인정한다(defaultNotifyVia 주석 참조 — 서버 사다리가 실제로 그 주소로 보낸다).
        notifyVia: defaultNotifyVia(memberDisplay),
      },
    ]);
  };

  /**
   * **이미 있는 칸**에 담당자를 지정한다(칸 추가가 아니다).
   *
   * `handleAddMember`는 목록 끝에 칸을 새로 만든다. 미지정 칸은 prefix 규칙상 뒤쪽에 모이므로,
   * 중간(예: 3칸 중 2번째)에 담당자를 채우려면 자리를 지정해 넣는 이 경로가 필요하다.
   * 라벨·순서·locked는 그대로 두고 담당자 관련 값만 교체한다.
   */
  const handleAssignMember = (index: number, memberId: string) => {
    const orgMember = availableMembers.find((m) => (m.member?.id ?? m.memberId ?? m.id) === memberId);
    if (!orgMember) return;
    const memberDisplay = extractMemberDisplay(orgMember);
    const newLines = [...editingLines];
    newLines[index] = {
      ...newLines[index],
      memberId: memberDisplay.id,
      member: memberDisplay,
      sourceType: 'member',
      // S76-A: 불러올 때의 지정 방식은 여기서 **끝난다** — 사용자가 특정 인물을 골랐으므로
      // 역할·위임 근거를 유지하면 그 선택이 저장에서 무시된다. 저장 경로는 이 값의 유무로
      // "손댔는가"를 판정하므로 반드시 지운다.
      _assignmentChain: undefined,
      // 담당자가 바뀌었으므로 채널 선택을 새 담당자의 보유 채널로 재시드(정본 축 notifyVia 하나)
      notifyVia: defaultNotifyVia(memberDisplay),
      _assigneeUnset: false,
    };
    setEditingLines(newLines);
  };

  /**
   * 이 칸의 담당자를 지정한다 — **작성자 본인**으로.
   * `assigneeSourceWhitelist: ['author']`인 칸(기본 메타의 '담당' 칸)은 이 경로로만 되돌릴 수 있다.
   * 실제 작성자 Member는 서버가 제출/저장 시 author chain으로 resolve하므로 여기서는 소스만 세운다.
   */
  const handleAssignAuthor = (index: number) => {
    const newLines = [...editingLines];
    newLines[index] = {
      ...newLines[index],
      memberId: undefined,
      member: undefined,
      sourceType: 'author',
      // S76-A: 작성자를 **명시적으로** 골랐다 — 역할·위임 근거를 지워야 저장이 그 선택을
      // 그대로 반영한다(남겨 두면 "안 바꿈"으로 읽혀 옛 체인이 되살아난다).
      _assignmentChain: undefined,
      notifyVia: undefined,
      _assigneeUnset: false,
    };
    setEditingLines(newLines);
  };

  /**
   * 담당자만 뗀다 — **칸은 남는다**(칸 제거는 handleRemoveApprover).
   * 남은 칸은 오프라인(수기) 결재 칸이 되어 출력물에 빈 서명란으로 남는다.
   * 알림 채널(notifyVia)도 함께 비운다 — 받을 사람이 없는 칸에 채널만 남기면
   * 다음 지정 때 옛 채널이 되살아난다.
   */
  const handleUnsetAssignee = (index: number) => {
    const newLines = [...editingLines];
    newLines[index] = {
      ...newLines[index],
      memberId: undefined,
      member: undefined,
      sourceType: undefined,
      // S76-A: 담당자를 뗐으므로 지정 방식 근거도 함께 뗀다 — 저장은 빈 체인으로 수렴한다.
      _assignmentChain: undefined,
      notifyVia: undefined,
      _assigneeUnset: true,
    };
    setEditingLines(newLines);
  };

  /**
   * **담당자 없는 칸을 목록 끝에 추가한다** — 종이(수기)로 결재할 칸.
   *
   * 결재란 추가와 담당자 지정은 별개 축인데(결정 2026-08-13), 작성 페이지에는 오래도록
   * "결재자 추가"(담당자 지정이 곧 칸 생성)만 있어 **칸만 늘리는 수단이 없었다.**
   * 그래서 미지정 칸이 하나라도 있으면 prefix 가드에 걸려 추가 자체가 막혔고,
   * 종이 결재용으로 칸을 더 두려는 사용자가 갈 곳이 없었다.
   *
   * 빈 칸은 목록 **끝**에 붙으므로 prefix(담당자는 앞에서부터 연속)를 어기지 않는다 —
   * 앞이 비어 있어도 뒤에 붙는 것 역시 비어 있기 때문. 그래서 이 액션은 미지정 칸이
   * 있어도 허용된다(담당자 지정 추가와 다른 점).
   */
  const handleAddUnassignedSlot = () => {
    setEditingLines([
      ...editingLines,
      {
        approvalLevel: editingLines.length + 1,
        label: '',
        _assigneeUnset: true,
      } as ApprovalLineItem,
    ]);
  };

  const handleRemoveApprover = (index: number) => {
    const newLines = editingLines
      .filter((_, i) => i !== index)
      .map((line, i) => ({ ...line, approvalLevel: i + 1 }));
    setEditingLines(newLines);
  };

  // ⚠️ 순서를 바꾼 뒤 level을 다시 매길 때 **행 객체를 직접 변이하지 않는다.**
  // `[...editingLines]`는 배열만 새로 만들고 원소는 그대로라, `line.approvalLevel = ...`은
  // 현재 상태(그리고 원본 `approvalLines`에서 파생된 객체)를 그 자리에서 고친다 — 취소해도
  // 되돌아오지 않고, 같은 객체를 보는 다른 파생값이 조용히 어긋난다.
  // :245 `handleRemoveApprover`가 이미 쓰는 불변 갱신과 같은 형태로 맞춘다.
  const relevel = (lines: ApprovalLineItem[]): ApprovalLineItem[] =>
    lines.map((line, i) => ({ ...line, approvalLevel: i + 1 }));

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newLines = [...editingLines];
    [newLines[index - 1], newLines[index]] = [newLines[index], newLines[index - 1]];
    setEditingLines(relevel(newLines));
  };

  const handleMoveDown = (index: number) => {
    if (index === editingLines.length - 1) return;
    const newLines = [...editingLines];
    [newLines[index], newLines[index + 1]] = [newLines[index + 1], newLines[index]];
    setEditingLines(relevel(newLines));
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => {
      const target = e.target as HTMLElement;
      target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newLines = [...editingLines];
    const [draggedItem] = newLines.splice(draggedIndex, 1);
    newLines.splice(dropIndex, 0, draggedItem);
    setEditingLines(relevel(newLines));
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleLabelChange = (index: number, value: string) => {
    const newLines = [...editingLines];
    newLines[index] = { ...newLines[index], label: value };
    setEditingLines(newLines);
  };

  /**
   * 채널 종류(SMS/이메일) 하나를 켜고 끈다 — 인코딩 단일화(2026-08-23) 이후 모든 칸이
   * 정본 축(`notifyVia: NotifyKind[]`) 하나만 쓴다.
   *
   * 마지막 하나는 끄지 못한다(기존 규칙 유지) — 채널을 전부 끄는 것은 "통지 안 함"이라
   * 담당자 해제와 구분되어야 하고, 그 UX는 F-F에서 경고와 함께 다룬다.
   */
  const handleToggleNotify = (index: number, kind: NotifyKind) => {
    const line = editingLines[index];
    const newLines = [...editingLines];

    const current =
      selectedNotifyKinds(line) ??
      defaultNotifyVia(line.member) ??
      [];
    if (current.includes(kind)) {
      if (current.length <= 1) return;
      newLines[index] = {
        ...line,
        notifyVia: current.filter((c) => c !== kind),
      };
    } else {
      newLines[index] = { ...line, notifyVia: [...current, kind] };
    }
    setEditingLines(newLines);
  };

  // 라벨 규칙은 공용(approvalLineIdentity) — 위임 모달의 가드 문구와 같은 이름을 써야 한다.
  const getStepLabel = (index: number): string => approvalStepLabel(editingLines, index);

  const getDefaultLabel = (index: number): string => `${index + 1}단계`;

  const updateEditingLine = (index: number, updates: Partial<ApprovalLineItem>) => {
    const newLines = [...editingLines];
    newLines[index] = { ...newLines[index], ...updates };
    setEditingLines(newLines);
  };

  return {
    editingLines,
    setEditingLines,
    isDirty,
    draggedIndex,
    dragOverIndex,
    initEdit,
    resetEdit,
    handleAddMember,
    handleAddUnassignedSlot,
    handleAssignMember,
    handleAssignAuthor,
    handleUnsetAssignee,
    handleRemoveApprover,
    handleMoveUp,
    handleMoveDown,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleLabelChange,
    handleToggleNotify,
    getStepLabel,
    getDefaultLabel,
    updateEditingLine,
  };
}
